import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Upload, FileText, Download, Trash2, FolderOpen, Image as ImageIcon, Pin, PinOff, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtSize = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b}B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)}KB`;
  return `${(b / (1024 * 1024)).toFixed(1)}MB`;
};

const DocumentsPanel = ({ propertyId, canEdit }) => {
  const [categories, setCategories] = useState([]);
  const [byCategory, setByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', category: 'site_maps', notes: '', file: null });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: cats }, { data: docs }] = await Promise.all([
        axios.get(`${API}/pm/document-categories`, { withCredentials: true }),
        axios.get(`${API}/pm/properties/${propertyId}/documents`, { withCredentials: true }),
      ]);
      setCategories(cats.categories || []);
      setByCategory(docs.by_category || {});
    } catch (e) {
      toast.error(e.response?.data?.detail?.code || 'Failed to load documents');
    }
    setLoading(false);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [{ data: cats }, { data: docs }] = await Promise.all([
          axios.get(`${API}/pm/document-categories`, { withCredentials: true }),
          axios.get(`${API}/pm/properties/${propertyId}/documents`, { withCredentials: true }),
        ]);
        if (!alive) return;
        setCategories(cats.categories || []);
        setByCategory(docs.by_category || {});
      } catch (e) {
        if (alive) toast.error(e.response?.data?.detail?.code || 'Failed to load documents');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [propertyId]);

  const upload = async () => {
    if (!form.file) { toast.error('Pick a file'); return; }
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('title', form.title);
      fd.append('category', form.category);
      fd.append('notes', form.notes || '');
      await axios.post(`${API}/pm/properties/${propertyId}/documents`, fd, { withCredentials: true });
      toast.success('Document uploaded');
      setOpen(false);
      setForm({ title: '', category: form.category, notes: '', file: null });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.code || 'Upload failed');
    }
    setBusy(false);
  };

  const download = async (doc) => {
    try {
      const res = await axios.get(`${API}/pm/properties/${propertyId}/documents/${doc.id}/download`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || doc.title || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const archive = async (doc) => {
    if (!window.confirm(`Archive "${doc.title}"?`)) return;
    try {
      await axios.delete(`${API}/pm/properties/${propertyId}/documents/${doc.id}`, { withCredentials: true });
      toast.success('Document archived');
      load();
    } catch { toast.error('Archive failed'); }
  };

  const togglePin = async (doc) => {
    try {
      await axios.post(`${API}/pm/properties/${propertyId}/documents/${doc.id}/pin`, {}, { withCredentials: true });
      toast.success(doc.pinned ? 'Unpinned' : 'Pinned to top');
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.code || 'Pin failed'); }
  };

  // Pinned across categories first
  const pinnedDocs = Object.values(byCategory).flat().filter((d) => d.pinned).sort((a, b) => (b.pinned_at || '').localeCompare(a.pinned_at || ''));

  return (
    <div data-testid="documents-panel" className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[#A3A3A3] text-xs">A tech on-site can pull up any of these in 2 clicks. Organize by category for quick access.</p>
        {canEdit && <Button data-testid="upload-doc-btn" onClick={() => setOpen(true)} size="sm" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#B8941F]"><Upload className="w-4 h-4 mr-1" /> Upload</Button>}
      </div>

      {loading ? <div className="p-6 text-[#A3A3A3]">Loading…</div> : (
        <div className="space-y-3">
          {categories.map((cat) => {
            const docs = byCategory[cat.key] || [];
            if (docs.length === 0) return null;
            return (
              <div key={cat.key} data-testid={`docs-cat-${cat.key}`} className="bg-[#141414] border border-[#262626] rounded-md p-4">
                <div className="flex items-center gap-2 mb-2">
                  <FolderOpen className="w-4 h-4 text-[#D4AF37]" />
                  <h4 className="text-white text-sm font-medium">{cat.label} <span className="text-[#525252]">({docs.length})</span></h4>
                </div>
                <ul className="space-y-1.5">
                  {docs.map((d) => (
                    <li key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center gap-3 p-2 bg-[#0A0A0A] border border-[#262626] rounded hover:border-[#D4AF37]/40">
                      {d.content_type?.startsWith('image/') ? <ImageIcon className="w-4 h-4 text-[#A3A3A3]" /> : <FileText className="w-4 h-4 text-[#A3A3A3]" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{d.title}</p>
                        <p className="text-[#525252] text-xs truncate">{d.filename} · {fmtSize(d.size_bytes)} · {new Date(d.uploaded_at).toLocaleDateString()} · {d.uploaded_by_name}</p>
                      </div>
                      <Button size="sm" variant="outline" className="border-[#404040] text-white h-7" data-testid={`download-doc-${d.id}`} onClick={() => download(d)}><Download className="w-3 h-3" /></Button>
                      {canEdit && <Button size="sm" variant="outline" className="border-red-500/50 text-red-300 hover:bg-red-500/10 h-7" data-testid={`archive-doc-${d.id}`} onClick={() => archive(d)}><Trash2 className="w-3 h-3" /></Button>}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
          {Object.keys(byCategory).length === 0 && (
            <div className="p-12 text-center text-[#525252] bg-[#141414] border border-[#262626] rounded-md" data-testid="docs-empty">
              <FileText className="w-8 h-8 mx-auto mb-2" />
              <p>No documents yet. {canEdit && 'Click "Upload" to add maps, plans, photos, or contracts.'}</p>
            </div>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload document</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">Max 20MB. Stored securely, available to NexFlow techs on-site.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title *</Label>
              <Input data-testid="doc-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Sewer Map - Main Line" className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="doc-category" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626] text-white">
                  {categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div>
              <Label>File *</Label>
              <input data-testid="doc-file" type="file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} className="block w-full text-sm text-[#A3A3A3] file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-[#D4AF37] file:text-[#0A0A0A] hover:file:bg-[#B8941F]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-[#404040] text-white" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button data-testid="confirm-upload" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#B8941F]" disabled={busy || !form.file || !form.title} onClick={upload}>Upload</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentsPanel;
